import PropTypes from 'prop-types';
import React, { useMemo, useRef } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useAddLocationParticipant, useFetchLocations } from 'api/locations/hooks';
import { useFetchRegions } from 'api/regions/hooks';
import { useInviteRetailer } from 'api/retailers/hooks';
import { Form, InfoTooltip, ModalForm } from 'components';
import { FormWrapper } from 'components/Modal/styles';
import { LOCATION_USER_ROLE_OPTIONS } from 'constants/locationUserRoles';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { mapInitialValues } from '../../utils/retailerFormUtils';

const socials = ['facebook', 'instagram', 'pinterest', 'twitter', 'tiktok'];

const renderRoleOptionLabel = (option, t) => (
  <span className="d-inline-flex align-items-center">
    {option.label}
    <InfoTooltip title={t(option.tooltipKey)} placement="right" />
  </span>
);

const RetailerForm = ({ type = 'create', initialValues, FormGroup, wrapperType }) => {
  const { t } = useTranslation(['pages', 'app', 'toastr']);
  const { t: tLocation } = useTranslation('ownerPortal', { keyPrefix: 'locations' });
  const { data: regions } = useFetchRegions();
  const { mutateAsync: inviteRetailer, isPending: isInvitingRetailer } = useInviteRetailer();
  const { mutateAsync: addLocationParticipant, isPending: isAddingLocationParticipant } =
    useAddLocationParticipant();
  const selectedRegion = useSelectedProfileStore((state) => state.selectedRegionUid);
  const formRef = useRef(null);

  const currentRegion = useMemo(
    () =>
      regions?.find(
        (region) =>
          region.uid === (type === 'update' ? initialValues?.region?.uid : selectedRegion),
      ) || regions?.[0],
    [initialValues?.region?.uid, regions, selectedRegion, type],
  );
  const mappedInitialValues = useMemo(
    () => ({
      ...mapInitialValues(initialValues),
      region_uid: currentRegion?.uid || '',
      regionName: currentRegion?.name || '',
      location_uid: '',
      location_role: '',
    }),
    [currentRegion, initialValues],
  );
  const { data: locations } = useFetchLocations(currentRegion?.uid, {
    enabled: !!currentRegion?.uid,
  });

  const locationOptions = useMemo(
    () =>
      (locations || []).map((location) => ({
        value: location.uid,
        label: location.name,
      })),
    [locations],
  );

  const handleSubmit = async ({ region_uid, email, location_uid, location_role }) => {
    const retailer = await inviteRetailer({ email, region_uid });

    if (location_uid && location_role) {
      await addLocationParticipant({
        locationUid: location_uid,
        content: {
          retailerUid: retailer.uid,
          role: location_role,
        },
      });
    }
  };

  return (
    <ModalForm
      innerRef={formRef}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'column',
        height: '100%',
      }}
      enableReinitialize
      initialValues={mappedInitialValues}
      onSubmit={handleSubmit}
      isLoading={isInvitingRetailer || isAddingLocationParticipant}
      ignoreClickedState
      isClosePopupOn={false}
    >
      {({ values }) => (
        <FormWrapper type={wrapperType === 'modal' ? 'create' : type}>
          <Row className="d-flex justify-content-center">
            <Col md={12}>
              <FormGroup required label={t('pages:retailers.email')}>
                <Form.Input type="text" name="email" disabled={type === 'update'} />
              </FormGroup>
              {type === 'update' && (
                <>
                  <FormGroup required label={t('pages:retailers.publisherId')}>
                    <Form.Input type="text" name="uid" />
                  </FormGroup>
                  <FormGroup required label={t('pages:retailers.name')}>
                    <Form.Input type="text" name="name" />
                  </FormGroup>
                  <FormGroup required label={t('pages:retailers.lastName')}>
                    <Form.Input type="text" name="lastName" />
                  </FormGroup>
                  <Form.Group>
                    <Form.Label>{t('pages:retailers.about')}</Form.Label>
                    <Form.Input as="textarea" name="about" style={{ maxHeight: '120px' }} />
                  </Form.Group>
                  {socials.map((social) => (
                    <FormGroup label={social} key={social}>
                      <Form.Input type="text" name={social} />
                    </FormGroup>
                  ))}
                </>
              )}
              <FormGroup label={t('pages:retailers.region')}>
                <Form.Input type="text" name="regionName" readOnly />
              </FormGroup>
              <LocationAssignmentSection>
                <LocationAssignmentHeader>
                  <LocationAssignmentTitle>
                    {t('pages:retailers.locationAssignmentTitle')}
                  </LocationAssignmentTitle>
                  <LocationAssignmentDescription>
                    {t('pages:retailers.locationAssignmentDescription')}
                  </LocationAssignmentDescription>
                </LocationAssignmentHeader>
                <FormGroup label={t('pages:retailers.location')}>
                  <Form.MultiSelect
                    options={locationOptions}
                    name="location_uid"
                    isMulti={false}
                    isClearable
                  />
                </FormGroup>
                {values.location_uid && (
                  <FormGroup required label={t('pages:retailers.locationRole')}>
                    <Form.Select
                      name="location_role"
                      options={LOCATION_USER_ROLE_OPTIONS}
                      formatOptionLabel={(option) => renderRoleOptionLabel(option, tLocation)}
                    />
                  </FormGroup>
                )}
              </LocationAssignmentSection>
            </Col>
          </Row>
        </FormWrapper>
      )}
    </ModalForm>
  );
};

const LocationAssignmentSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #dee2e6;
`;

const LocationAssignmentHeader = styled.div`
  margin-bottom: 16px;
  text-align: left;
`;

const LocationAssignmentTitle = styled.h3`
  font-size: 16px;
  font-weight: bold;
`;

const LocationAssignmentDescription = styled.p`
  margin: 0;
  color: #6c757d;
  font-size: 14px;
`;

RetailerForm.propTypes = {
  type: PropTypes.oneOf(['create', 'update']),
  initialValues: PropTypes.object,
  FormGroup: PropTypes.func,
  wrapperType: PropTypes.string,
};

export default RetailerForm;
